import { Legislation } from "@/models/legislation/Legislation";
import DocumentReference from "@/models/conversation/document";
import { logger } from "@/lib/logger";
import { ST_SOURCES_BASE_URL } from "@/services/appServices";

const readStringProp = (
  raw: Record<string, unknown>,
  ...keys: string[]
): string | undefined => {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === "string" && v.length > 0) {
      return v;
    }
  }
  return undefined;
};

/**
 * Converts a source string to a valid Azure blob container name.
 * Container names must be lowercase and can only contain a-z, 0-9, and hyphens.
 */
export const convertSourceToBlobContainerName = (source: string): string => {
  return source
    .toLowerCase()
    .replace(/æ/g, "ae")
    .replace(/ø/g, "oe")
    .replace(/å/g, "aa")
    .replace(/[^a-z0-9-]/g, "");
};

/**
 * Build a PDF URL from a document reference (chat context).
 * Uses the document reference's source and documentId to construct the URL.
 * @param baseUrl — defaults to the environment's sources storage account
 *                  (`ST_SOURCES_BASE_URL`; override in tests).
 */
export const buildPdfUrlFromReference = (
  documentReference: DocumentReference,
  baseUrl: string = ST_SOURCES_BASE_URL
): string => {
  const containerName = convertSourceToBlobContainerName(
    documentReference.source || ""
  );
  return `${baseUrl}/${containerName}/${documentReference.documentId}.pdf`;
};

/**
 * Where a legislation's PDF lives. A `storage` path such as
 * `eurlex/{id}/da/pdf/{file}.pdf` is a blob in the data-sources storage
 * account, which refuses anonymous reads: the browser can only load it through
 * a short-lived link the API signs (`useLegislationPdfUrl`, LAR-542). A `url`
 * is on another host and is loaded as it is.
 */
export type LegislationPdfSource =
  { kind: "storage"; path: string } | { kind: "url"; url: string };

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

/**
 * Resolve the PDF from EUR-Lex legislation metadata (ingestion stores PascalCase
 * keys): a stored path or URL in `PdfUrl`, otherwise the EUR-Lex CELEX URL.
 */
const extractEurLexPdfSource = (
  legislation: Legislation
): LegislationPdfSource | null => {
  try {
    if (!legislation.metadataObject) {
      return null;
    }

    const raw = JSON.parse(legislation.metadataObject) as Record<
      string,
      unknown
    >;

    const pdfFromMetadata = readStringProp(raw, "pdfUrl", "PdfUrl");
    if (pdfFromMetadata) {
      return isAbsoluteUrl(pdfFromMetadata)
        ? { kind: "url", url: pdfFromMetadata }
        : { kind: "storage", path: pdfFromMetadata };
    }

    // CELEX lives in metadata and/or `legislation_external_id` — never use `externalId`
    // (EUR-Lex cellar GUID), which would produce invalid `CELEX:<guid>` URLs.
    const celexNumber =
      readStringProp(raw, "celexNumber", "CelexNumber") ??
      (legislation.legislationExternalId?.trim() || undefined);
    if (celexNumber) {
      const lang = readStringProp(raw, "language", "Language") ?? "DA";
      return {
        kind: "url",
        url: `https://eur-lex.europa.eu/legal-content/${lang}/TXT/PDF/?uri=CELEX:${celexNumber}`,
      };
    }

    return null;
  } catch (error) {
    logger.warn("Failed to parse EUR-Lex metadata for PDF URL extraction", {
      operation: "extractEurLexPdfUrl",
      externalId: legislation.externalId,
      datasource: legislation.datasource,
      error,
    });
    return null;
  }
};

export type ResolveLegislationPdfSourceOptions = {
  /**
   * When true, log a warning if no PDF can be resolved. Use only from dedicated PDF routes
   * to avoid noise when the helper runs during render for optional controls (e.g. download).
   */
  logWhenUnavailable?: boolean;
};

/**
 * Where a legislation's PDF lives (legislation context).
 * Prefers stored blob paths (embeddable) over external EUR-Lex `downloadLink`.
 */
export const resolveLegislationPdfSource = (
  legislation: Legislation,
  options?: ResolveLegislationPdfSourceOptions
): LegislationPdfSource | null => {
  if (legislation.pdfUrls && legislation.pdfUrls.length > 0) {
    return { kind: "storage", path: legislation.pdfUrls[0] };
  }

  const isEurLex =
    legislation.datasource?.toLowerCase() === "eur-lex" ||
    legislation.datasource?.toLowerCase() === "eurlex";

  if (isEurLex) {
    const eurLexSource = extractEurLexPdfSource(legislation);
    if (eurLexSource) {
      return eurLexSource;
    }
  }

  if (legislation.downloadLink) {
    return { kind: "url", url: legislation.downloadLink };
  }

  if (options?.logWhenUnavailable) {
    logger.warn("No PDF URL available for legislation document", {
      operation: "buildPdfUrlFromLegislation",
      externalId: legislation.externalId,
      legislationExternalId: legislation.legislationExternalId ?? null,
      datasource: legislation.datasource,
      hasDownloadLink: !!legislation.downloadLink,
      hasPdfUrls: !!(legislation.pdfUrls && legislation.pdfUrls.length > 0),
      hasMetadata: !!legislation.metadataObject,
    });
  }

  return null;
};
